import {
  DirectionalHint,
  FontWeights,
  makeStyles,
  Stack,
  Text,
  TooltipHost,
} from '@fluentui/react';
import {
  FormHookDropdown,
  FormHookTextField,
} from 'common/components/FormHooksFields';
import { FormHookCheckBox } from 'common/components/FormHooksFields/FormHookCheckBox';
import { formatDropdownOptions } from 'common/utils';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { DataAccessPolicyCommonData_companyDepartmentGroups } from '../__generated__/DataAccessPolicyCommonData';
import { DataAccessPolicyValues } from '../types';
import { useStyles } from './index.styles';
import { useCommonStyles } from 'common/styles';

const ADDITION_TAG_TEXT =
  'When selected, users assigned to this policy will only be able to view transactions that have an associated viewer tag (e.g., Auditor Tag).';
const DEPARTMENT_ISOLATION_TEXT =
  'Department Isolation (per module) is used to limit which departments a user can enter transactions against. If no restrictions are needed, leave unchecked.';
const UNLIMITED_ACCESS_TEXT =
  'Allows unlimited access to any of the following categories.';
const GLOBAL_DOCUMENT_POOL_ACCESS_TEXT =
  'When selected, users with strict department access will be able to use document pools that do not have department designations.';
const LIMIT_ACCESS =
  'When checked, the user will only be allowed to create or request approval for transactions that belong to departments the user is assigned to.';
const DEPARTMENT_GROUP_OVERRIDE =
  'When selected, the user will only be allowed to create or request approval for transactions associated with departments in the overriding department group.';

interface BasicFormProps {
  companyDepartmentGroups:
    | DataAccessPolicyCommonData_companyDepartmentGroups
    | null
    | undefined;
  _isUpdatable: boolean;
}

export const BasicForm: React.FC<BasicFormProps> = ({
  companyDepartmentGroups,
  _isUpdatable,
}) => {
  const styles = useStyles();
  const commonStyles = useCommonStyles();

  const { watch } = useFormContext<DataAccessPolicyValues>();

  const companyDepartmentGroupOptions = formatDropdownOptions(
    companyDepartmentGroups?.nodes,
    {
      getKey: (item) => item.departmentGroupId,
      getText: (item) => item.departmentGroup?.name || '',
    }
  );

  return (
    <Stack
      tokens={{
        padding: 20,
        childrenGap: 20,
      }}
    >
      <FormHookTextField
        label="Policy Name"
        name="name"
        disabled={!_isUpdatable}
      />
      <FormHookTextField
        label="Description"
        name="description"
        disabled={!_isUpdatable}
      />
      <Stack horizontal className={styles.margin20}>
        <Stack className={styles.flex1}>
          <TooltipHost content={ADDITION_TAG_TEXT}>
            <FormHookCheckBox
              name="isAdditionalTagAccessRequired"
              label='Transaction isolation "Auditor Tag Access"'
              disabled={!_isUpdatable}
            />
          </TooltipHost>
        </Stack>
        <Stack className={styles.flex1}>
          <TooltipHost content={GLOBAL_DOCUMENT_POOL_ACCESS_TEXT}>
            <FormHookCheckBox
              name="isDocumentPoolNullDepartmentAccessible"
              label="Global Document Pool Access"
              disabled={!_isUpdatable}
            />
          </TooltipHost>
        </Stack>
      </Stack>
      <TooltipHost
        content={DEPARTMENT_ISOLATION_TEXT}
        directionalHint={DirectionalHint.topAutoEdge}
      >
        <Text variant="xLarge">Department Isolation</Text>
      </TooltipHost>
      <Stack
        horizontal
        tokens={{ childrenGap: 20, padding: '10px 0px 5px' }}
        verticalAlign="center"
      >
        <Stack.Item className={styles.flex50}>
          <TooltipHost
            content={LIMIT_ACCESS}
            directionalHint={DirectionalHint.topAutoEdge}
          >
            <Text variant="medium" className={commonStyles.semibold}>
              Limit Access to Assigned Department(s)
            </Text>
          </TooltipHost>
        </Stack.Item>
        <Stack.Item className={styles.flex50}>
          <TooltipHost content={DEPARTMENT_GROUP_OVERRIDE}>
            <Text variant="medium" className={commonStyles.semibold}>
              Department Group Override
            </Text>
          </TooltipHost>
        </Stack.Item>
      </Stack>
      <Stack horizontal tokens={{ childrenGap: 20 }} verticalAlign="center">
        <Stack.Item className={styles.flex50}>
          <FormHookCheckBox
            name="isDocDocumentAccess"
            label="Document Pools"
            disabled={!!watch('documentsDepartmentGroupId') || !_isUpdatable}
          />
        </Stack.Item>
        <Stack.Item className={styles.flex50}>
          <FormHookDropdown
            placeholder="Select"
            options={companyDepartmentGroupOptions}
            name="documentsDepartmentGroupId"
            disabled={!!watch('isDocDocumentAccess') || !_isUpdatable}
          />
        </Stack.Item>
      </Stack>
      <Stack horizontal tokens={{ childrenGap: 20 }} verticalAlign="center">
        <Stack.Item className={styles.flex50}>
          <FormHookCheckBox
            name="isPoDocumentAccess"
            label="Purchase Orders"
            disabled={
              !!watch('purchaseOrderDepartmentGroupId') || !_isUpdatable
            }
          />
        </Stack.Item>
        <Stack.Item className={styles.flex50}>
          <FormHookDropdown
            placeholder="Select"
            options={companyDepartmentGroupOptions}
            name="purchaseOrderDepartmentGroupId"
            disabled={!!watch('isPoDocumentAccess') || !_isUpdatable}
          />
        </Stack.Item>
      </Stack>
      <Stack horizontal tokens={{ childrenGap: 20 }} verticalAlign="center">
        <Stack.Item className={styles.flex50}>
          <FormHookCheckBox
            name="isTransDocumentAccess"
            label="Transaction Signing"
            disabled={!!watch('transactionsDepartmentGroupId') || !_isUpdatable}
          />
        </Stack.Item>
        <Stack.Item className={styles.flex50}>
          <FormHookDropdown
            placeholder="Select"
            options={companyDepartmentGroupOptions}
            name="transactionsDepartmentGroupId"
            disabled={!!watch('isTransDocumentAccess') || !_isUpdatable}
          />
        </Stack.Item>
      </Stack>
      <Stack horizontal tokens={{ childrenGap: 20 }} verticalAlign="center">
        <Stack.Item className={styles.flex50}>
          <FormHookCheckBox
            name="isTaDocumentAccess"
            label="Travel Authorization"
            disabled={
              !!watch('travelAuthorizationDepartmentGroupId') || !_isUpdatable
            }
          />
        </Stack.Item>
        <Stack.Item className={styles.flex50}>
          <FormHookDropdown
            placeholder="Select"
            options={companyDepartmentGroupOptions}
            name="travelAuthorizationDepartmentGroupId"
            disabled={!!watch('isTaDocumentAccess') || !_isUpdatable}
          />
        </Stack.Item>
      </Stack>
      <TooltipHost
        content={UNLIMITED_ACCESS_TEXT}
        directionalHint={DirectionalHint.topAutoEdge}
      >
        <Text variant="xLarge">Unlimited Access</Text>
      </TooltipHost>
      <Stack
        horizontal
        tokens={{ childrenGap: 20, padding: '10px 0px' }}
        verticalAlign="center"
      >
        <Stack.Item className={styles.flex25}>
          <FormHookCheckBox
            name="isAllBusinessUnits"
            label="Business Units"
            disabled={!_isUpdatable}
          />
        </Stack.Item>
        <Stack.Item className={styles.flex25}>
          <FormHookCheckBox
            name="isAllWorkgroups"
            label="Work Groups"
            disabled={!_isUpdatable}
          />
        </Stack.Item>
        <Stack.Item className={styles.flex25}>
          <FormHookCheckBox
            name="isAllTagAccess"
            label="Tags"
            disabled={!_isUpdatable}
          />
        </Stack.Item>
        <Stack.Item className={styles.flex25}>
          <FormHookCheckBox
            name="isAllProjects"
            label="Projects"
            disabled={!_isUpdatable}
          />
        </Stack.Item>
      </Stack>
    </Stack>
  );
};
